import type { InvoicePropertiesResult } from '@centecomm/portal-service';
import { useInvoices } from '@centecomm/portal-service-react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { LoadStatus } from '../../../Types';
import type { InvoicesActionTypes } from '../actions';
import { InvoicesActions } from '../actions';
import { propertiesLoadStatus } from '../selectors';
import type { InvoiceProperty } from '../Types/InvoiceProperty';

type PropertiesStateResult = {
    areLoaded: boolean;
    areLoading: boolean;
    hasError: boolean;
    loadState: LoadStatus;
};

// eslint-disable-next-line import/prefer-default-export
export const usePropertiesFromInvoicesState = (enabled = true): PropertiesStateResult => {
    const dispatch = useDispatch();

    const { getInvoiceProperties } = useInvoices();

    const loadState: LoadStatus = useSelector(propertiesLoadStatus);

    React.useEffect(() => {
        if (!enabled) return;

        if (loadState === LoadStatus.INITIAL_STATE || loadState === LoadStatus.ERROR) {
            dispatch({
                type: InvoicesActions.LOAD_INVOICE_PROPERTIES,
            });

            getInvoiceProperties()
                .then((properties: InvoicePropertiesResult[]) => {
                    dispatch({
                        type: InvoicesActions.SET_INVOICE_PROPERTIES,
                        payload: properties.map(
                            ({ id, name, occupants, company }) =>
                                ({
                                    id,
                                    name,
                                    occupants: occupants.map((occupant) => ({
                                        id: occupant.id,
                                        name: occupant.name,
                                    })),
                                    company: {
                                        id: company.id,
                                        name: company.name,
                                        paymentProviderIde: company.paymentProviderIde,
                                    },
                                } as InvoiceProperty),
                        ),
                    } as InvoicesActionTypes);
                })
                .catch((error) => {
                    dispatch({
                        type: InvoicesActions.SET_INVOICE_PROPERTIES_ERROR_STATE,
                        payload: {
                            errorMessage: error?.code ? error.code : 'We were not able to load properties.',
                        },
                    });
                });
        }
    }, [loadState, enabled]);

    return {
        areLoaded: loadState === LoadStatus.LOADED,
        areLoading: loadState === LoadStatus.PENDING,
        hasError: loadState === LoadStatus.ERROR,
        loadState,
    };
};
